import { useRef, useEffect, useState } from "react";
import { UploadCloud, Camera, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { PhotoPreviewGrid } from "./PhotoPreviewGrid";
import { PhotoViewer } from "./PhotoViewer";
import { MAX_PHOTOS } from "../../hooks/usePhotoUpload";

const ACCEPT = ".jpg,.jpeg,.png,.heic,.heif,image/jpeg,image/png,image/heic,image/heif";

export default function PhotoUpload({ photoUpload, onContinue, onSkip }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(null);
  const {
    photos,
    addFiles,
    removePhoto,
    setCaption,
    saveCaption,
    retryUpload,
    isUploading,
    uploadedPhotos,
  } = photoUpload;

  // Warn before leaving the page while uploads are still in progress
  useEffect(() => {
    const warn = (e) => {
      if (isUploading) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isUploading]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(e.dataTransfer.files);
  };

  const handleInputChange = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  const viewerPhotos = photos
    .filter((p) => p.previewUrl)
    .map((p) => ({ src: p.previewUrl, caption: p.caption, name: p.name }));

  const openViewer = (gridIdx) => {
    const photo = photos[gridIdx];
    const viewerIdx = photos.filter((p) => p.previewUrl).findIndex((p) => p.localId === photo.localId);
    if (viewerIdx >= 0) setViewerIndex(viewerIdx);
  };

  const atLimit = photos.filter((p) => p.status !== "error").length >= MAX_PHOTOS;

  return (
    <div className="animate-fade-in" data-testid="photo-upload-step">
      <h1 className="font-manrope font-bold text-2xl sm:text-3xl text-[#1F2933] text-center mb-2">
        Add Photos of Your Project
      </h1>
      <p className="text-[#0FA3B1] font-medium text-center mb-3">Optional, but recommended</p>
      <p className="text-[#1F2933]/60 text-sm text-center mb-8 max-w-lg mx-auto">
        Photos help contractors better understand the project and may result in a more accurate
        estimate. Upload close-up photos, wider views of the area, existing fixtures, damage,
        measurements, or anything else relevant.
      </p>

      {/* Drop zone */}
      {!atLimit && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors mb-6 ${
            dragActive ? "border-[#0FA3B1] bg-[#0FA3B1]/5" : "border-gray-300 bg-gray-50"
          }`}
          data-testid="photo-drop-zone"
        >
          <UploadCloud className="w-10 h-10 text-[#0FA3B1] mx-auto mb-3" />
          <p className="font-medium text-[#1F2933] mb-1">Drag and drop photos here</p>
          <p className="text-xs text-[#1F2933]/50 mb-4">
            JPG, PNG, or HEIC • up to 10 MB each • max {MAX_PHOTOS} photos
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white rounded-xl px-6"
              data-testid="choose-photos-btn"
            >
              Choose Photos
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => cameraInputRef.current?.click()}
              className="rounded-xl px-6 sm:hidden"
              data-testid="take-photo-btn"
            >
              <Camera className="w-4 h-4 mr-2" /> Take a Photo
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            onChange={handleInputChange}
            data-testid="photo-file-input"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleInputChange}
            data-testid="photo-camera-input"
          />
        </div>
      )}

      {atLimit && (
        <p className="text-sm text-amber-600 text-center mb-4" data-testid="photo-limit-message">
          You've reached the maximum of {MAX_PHOTOS} photos. Remove a photo to add another.
        </p>
      )}

      {/* Previews */}
      {photos.length > 0 && (
        <div className="mb-6">
          <p className="text-sm font-medium text-[#1F2933]/70 mb-3" data-testid="photo-count-label">
            {uploadedPhotos.length} photo{uploadedPhotos.length !== 1 ? "s" : ""} uploaded
            {isUploading ? " (uploading…)" : ""}
          </p>
          <PhotoPreviewGrid
            photos={photos}
            onRemove={removePhoto}
            onCaptionChange={setCaption}
            onCaptionSave={saveCaption}
            onRetry={retryUpload}
            onView={openViewer}
          />
        </div>
      )}

      <Button
        onClick={onContinue}
        disabled={isUploading}
        className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl disabled:opacity-50"
        data-testid="photo-continue-btn"
      >
        {isUploading ? "Uploading photos…" : "Continue"} <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
      <button
        type="button"
        onClick={onSkip}
        disabled={isUploading}
        className="block w-full text-center mt-4 text-sm text-[#1F2933]/60 hover:text-[#0FA3B1] underline disabled:opacity-50"
        data-testid="photo-skip-btn"
      >
        Skip for now
      </button>

      <PhotoViewer
        photos={viewerPhotos}
        index={viewerIndex}
        onClose={() => setViewerIndex(null)}
        onIndexChange={setViewerIndex}
      />
    </div>
  );
}
